import * as React from 'react';
import { Card } from 'components/ui/CardGs';
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from 'components/ui/tooltip';
import { cn } from 'lib/utils';

type RootProps = {
  loading?: boolean;
  children: React.ReactNode;
};

const CardRoot = ({ loading, children }: RootProps) => {
  return (
    <div className={cn('opacity-100', loading && 'opacity-30')}>
      <Card.Root>{children}</Card.Root>
    </div>
  );
};

type HeaderProps = {
  title: React.ReactNode;
  icon: React.ReactNode;
};

const CardHeader = ({ title, icon }: HeaderProps) => {
  return (
    <Card.Header>
      <div className={'flex items-center justify-between w-full'}>
        <h5 className={'font-GilroySemibold text-sm text-slate-800'}>{title}</h5>
        {icon}
      </div>
    </Card.Header>
  );
};

type BodyProps = {
  children: React.ReactNode;
};

const CardBody = ({ children }: BodyProps) => {
  return (
    <Card.Content>
      <div className={'flex flex-col gap-2'}>{children}</div>
    </Card.Content>
  );
};

type BodyHeadLineProps = {
  count?: number;
  percentage?: number;
};

const CardBodyHeadLine = ({ count, percentage }: BodyHeadLineProps) => {
  return (
    <div className={'flex items-center justify-between font-GilroyBold text-3xl text-orange-500'}>
      <span>{count || 0}</span>
      <span>{Math.round(Number(percentage || 0))}%</span>
    </div>
  );
};

type CardBodyBottomLineProps = {
  children: React.ReactNode;
};

const CardBodyBottomLine = ({ children }: CardBodyBottomLineProps) => {
  return <div className={'flex items-center gap-2'}>{children}</div>;
};

type CardActionButtonProps = {
  onClick: () => void;
  children: React.ReactNode;
  tooltip?: string | number;
  active: boolean;
};

const CardActionButton = ({
                            onClick,
                            children,
                            tooltip,
                            active,
                          }: CardActionButtonProps) => {
  if (tooltip === undefined) {
    return (
      <button
        onClick={onClick}
        className={cn(
          'rounded-md bg-slate-200 px-1 py-0.5 font-GilroySemibold text-xs text-slate-500 max-w-max',
        )}
      >
        {children}
      </button>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          onClick={onClick}
          className={cn(
            'rounded-md bg-slate-200 px-2 py-0.5 font-GilroySemibold text-xs text-slate-500 max-w-max',
            active && 'bg-slate-600 text-white',
          )}
        >
          {children}
        </TooltipTrigger>
        <TooltipContent side={'top'} className="bg-slate-500 text-white">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const IndicatorCard = {
  Root: CardRoot,
  Header: CardHeader,
  Body: CardBody,
  BodyHeadLine: CardBodyHeadLine,
  BodyBottomLine: CardBodyBottomLine,
  ActionButton: CardActionButton,
};
